import { registrableDomain } from './registrable-domain';

/**
 * What a whitelist entry means, and what it covers. One entry excuses a domain
 * for every client, so "what will this cover" is answered by the same rule in
 * the panel that types it and in the sweep that obeys it — here, because the
 * panel is a browser bundle and the sweep is a worker.
 */

/** The three states a request can be in, and the colour each is read in. */
export const WHITELIST_STATUS = {
  whitelisted: { label: 'whitelisted', tone: 'resolved' },
  proposed: { label: 'proposed', tone: 'attention' },
  declined: { label: 'declined', tone: 'danger' },
} as const;

export type WhitelistStatus = keyof typeof WHITELIST_STATUS;

/**
 * Why a domain is excused, as words rather than free text: typed by hand, one
 * decision reads back as "media", "news site" and "press", which is a column
 * nobody can group. The field still stores a string, so older rows keep theirs.
 */
export const WHITELIST_REASONS = [
  'government or public body',
  'university or research',
  'news or media',
  'search engine',
  'our own site',
  'the client’s own platform',
  'social network',
  'moderated marketplace or store',
  'false positive',
] as const;

/** What kind of rule an entry is, which decides what it covers. */
export type WhitelistRuleKind = 'domain' | 'suffix' | 'label';

export interface WhitelistRule {
  /** Stored exactly as typed here, so the row and the rule are one string. */
  value: string;
  kind: WhitelistRuleKind;
  /** The subdomain that was folded away, when one was. */
  folded: string | null;
}

/**
 * A typed address as the rule it becomes, or null when it could not be one. A
 * subdomain folds to its registrable domain, because a rule on one host leaves
 * the rest of the site filed against; `*.gov.*` stays a pattern, since one
 * rule per registry beats a row per country nobody can finish.
 */
export function whitelistRuleFrom(raw: string): WhitelistRule | null {
  const typed = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .split('/')[0]
    .split('?')[0]
    .split(':')[0]
    .replace(/\.$/, '');
  if (!typed) return null;

  // A pattern: `*.gov.*`, `*.mil.*`. What matters is the label in the middle,
  // and the stars say "wherever it sits in the name".
  if (typed.includes('*')) {
    const label = typed.replace(/\*/g, '').replace(/^\.+|\.+$/g, '');
    if (!/^[a-z0-9-]+$/.test(label)) return null;
    return { value: `*.${label}.*`, kind: 'label', folded: null };
  }

  // A suffix rule as the seeded list writes them: `.ac.uk` covers every host
  // under it and the bare suffix itself.
  if (typed.startsWith('.')) {
    const suffix = typed.replace(/^\.+/, '');
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*$/.test(suffix)) return null;
    return { value: `.${suffix}`, kind: 'suffix', folded: null };
  }

  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(typed)) return null;
  const base = registrableDomain(typed);
  return { value: base, kind: 'domain', folded: base === typed ? null : typed };
}

/** What kind of rule a stored entry is, read back off the string. */
export function whitelistRuleKind(value: string): WhitelistRuleKind {
  if (value.includes('*')) return 'label';
  if (value.startsWith('.')) return 'suffix';
  return 'domain';
}

/**
 * Does this entry cover that host? A domain rule matches it or anything under a
 * dot of it (`endsWith` alone says `notyoutube.com` is YouTube); a suffix rule
 * matches the suffix and everything under it; a label rule matches a
 * registry-level `gov`, and never `gov.example.com`.
 */
export function whitelistCovers(entry: string, host: string): boolean {
  const name = host.trim().toLowerCase().replace(/\.$/, '');
  const rule = entry.trim().toLowerCase();
  if (!name || !rule) return false;

  if (whitelistRuleKind(rule) === 'label') {
    const label = rule.replace(/\*/g, '').replace(/^\.+|\.+$/g, '');
    const labels = name.split('.').filter(Boolean);
    // The last label or the one before it: that is the part of a name a
    // registry hands out, and the only place `gov` means a government.
    return labels.slice(-2).includes(label) && labels.length >= 2;
  }

  if (rule.startsWith('.')) {
    const suffix = rule.slice(1);
    return name === suffix || name.endsWith(`.${suffix}`);
  }

  return name === rule || name.endsWith(`.${rule}`);
}

/** The same question against a whole list, which is how a sweep asks it. */
export function whitelistedBy(host: string, entries: readonly string[]): string | null {
  return entries.find((entry) => whitelistCovers(entry, host)) ?? null;
}
